import React, { useContext, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Table, FormGroup, Label, Input, Button } from 'reactstrap';
import Select from 'react-select';
import { MdDeleteOutline, MdEdit } from 'react-icons/md';
import { toast } from 'react-toastify';
import { SrxApi } from '../../../../api/SrxApi';
import { ButtonLoader, DataFetchLoader } from '../../../../components/Loader.js';
import { confirm } from '../../../../utility-components/ConfirmModal';
import { DATA_TYPES, INTERNAL_NAME_PATTERN } from '../pharmacyReportsHelpers.js';
import { UserContext } from '../../../../contexts/UserContext';
import { hasRolePermission } from '../../../../utility-services/rolePermissions.js';

const dataTypeOptions = DATA_TYPES.map((type) => ({ value: type, label: type }));

const EMPTY_OPTION = { value: '', label: '' };

const EMPTY_FORM = { name: '', internalName: '', dataType: 'List', options: [EMPTY_OPTION] };

function toForm(sharedParameter) {
    return {
        name: sharedParameter.name || '',
        internalName: sharedParameter.internalName || '',
        dataType: sharedParameter.dataType || 'List',
        options: sharedParameter.options?.length
            ? sharedParameter.options.map((option) => ({ value: option.value, label: option.label }))
            : [EMPTY_OPTION],
    };
}

function getFormError(form) {
    if (!form.name.trim()) return 'Enter a name.';
    if (!form.internalName.trim()) return 'Enter an internal name.';
    if (!INTERNAL_NAME_PATTERN.test(form.internalName.trim()))
        return `Internal name '${form.internalName.trim()}' can only have lowercase letters, numbers and underscores.`;
    if (form.dataType !== 'List') return null;
    if (form.options.length === 0) return 'Add at least one option.';

    const index = form.options.findIndex((option) => !option.value.trim() || !option.label.trim());
    if (index !== -1) return `Enter a value and a label for option ${index + 1}.`;

    const commaIndex = form.options.findIndex((option) => option.value.includes(','));
    if (commaIndex !== -1) return `The value of option ${commaIndex + 1} can't contain a comma.`;

    const values = form.options.map((option) => option.value.trim());
    const duplicate = values.find((value, i) => values.indexOf(value) !== i);
    return duplicate ? `Option '${duplicate}' is added more than once.` : null;
}

function toPayload(form) {
    return {
        name: form.name.trim(),
        internalName: form.internalName.trim(),
        dataType: form.dataType,
        options:
            form.dataType === 'List'
                ? form.options.map((option) => ({ value: option.value.trim(), label: option.label.trim() }))
                : [],
    };
}

function SharedParametersModal({ isOpen, onClose, onSharedParametersChanged }) {
    const { roles } = useContext(UserContext);
    const canEdit = hasRolePermission(roles, 'canEditManageReports');
    const canDelete = hasRolePermission(roles, 'canDeleteManageReports');
    const [sharedParameters, setSharedParameters] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [originalForm, setOriginalForm] = useState(EMPTY_FORM);
    const [isSaving, setIsSaving] = useState(false);

    const isEditing = editingId != null;

    const loadSharedParameters = () => {
        setIsLoading(true);
        SrxApi.get('/internalreports/sharedparameters')
            .then((res) => setSharedParameters(Array.isArray(res?.data) ? res.data : []))
            .catch((err) => {
                toast.error(err?.response?.data?.message || 'Error retrieving shared parameters: ' + err.message);
                setSharedParameters([]);
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        if (isOpen) loadSharedParameters();
    }, [isOpen]);

    const closeForm = () => {
        setIsFormOpen(false);
        setEditingId(null);
        setForm(EMPTY_FORM);
    };

    const openAddForm = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
        setIsFormOpen(true);
    };

    const openEditForm = (sharedParameter) => {
        const values = toForm(sharedParameter);
        setEditingId(sharedParameter.internalReportSharedParameterId);
        setForm(values);
        setOriginalForm(values);
        setIsFormOpen(true);
    };

    const handleFieldChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleOptionChange = (index, field, value) => {
        setForm((prev) => ({
            ...prev,
            options: prev.options.map((option, i) => (i === index ? { ...option, [field]: value } : option)),
        }));
    };

    const addOption = () => {
        setForm((prev) => ({ ...prev, options: [...prev.options, EMPTY_OPTION] }));
    };

    const removeOption = (index) => {
        setForm((prev) => ({ ...prev, options: prev.options.filter((option, i) => i !== index) }));
    };

    const handleSave = () => {
        const formError = getFormError(form);
        if (formError) {
            toast.error(formError);
            return;
        }

        setIsSaving(true);
        const payload = toPayload(form);
        const request = isEditing
            ? SrxApi.post(`/internalreports/sharedparameters/${editingId}`, payload)
            : SrxApi.post('/internalreports/sharedparameters', payload);

        request
            .then(() => {
                toast.success(isEditing ? 'Shared parameter updated.' : 'Shared parameter added.');
                closeForm();
                loadSharedParameters();
                if (onSharedParametersChanged) onSharedParametersChanged();
            })
            .catch((err) =>
                toast.error(err?.response?.data?.message || 'Error saving shared parameter: ' + err.message)
            )
            .finally(() => setIsSaving(false));
    };

    const handleDelete = async (sharedParameter) => {
        if (!(await confirm(`Delete <i>${sharedParameter.name}</i>?`, 'Delete Shared Parameter', 'Delete', 'Cancel')))
            return;

        SrxApi.delete(`/internalreports/sharedparameters/${sharedParameter.internalReportSharedParameterId}`)
            .then(() => {
                toast.success('Shared parameter deleted.');
                if (editingId === sharedParameter.internalReportSharedParameterId) {
                    closeForm();
                }
                loadSharedParameters();
                if (onSharedParametersChanged) onSharedParametersChanged();
            })
            .catch((err) =>
                toast.error(err?.response?.data?.message || 'Error deleting shared parameter: ' + err.message)
            );
    };

    const closeModal = () => {
        closeForm();
        onClose();
    };

    const isUnchanged = isEditing && JSON.stringify(form) === JSON.stringify(originalForm);
    const formError = isFormOpen ? getFormError(form) : null;

    return (
        <Modal isOpen={isOpen} toggle={closeModal} size="lg" centered>
            <ModalHeader toggle={closeModal}>Shared Parameters</ModalHeader>
            <ModalBody>
                {isLoading ? (
                    <DataFetchLoader name="Loading shared parameters..." />
                ) : (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <span className="text-muted">
                                Create a parameter once and pick it in any report from the parameter Type list.
                            </span>
                            {!isFormOpen && (
                                <Button color="primary" size="sm" onClick={openAddForm}>
                                    Add Shared Parameter
                                </Button>
                            )}
                        </div>

                        {isFormOpen && (
                            <div className="border rounded p-3 mb-3">
                                <div className="d-flex flex-wrap" style={{ gap: '16px' }}>
                                    <FormGroup style={{ flex: '1 1 200px' }}>
                                        <Label>Name</Label>
                                        <Input
                                            maxLength={100}
                                            placeholder="Alert Widgets"
                                            value={form.name}
                                            onChange={(e) => handleFieldChange('name', e.target.value)}
                                        />
                                    </FormGroup>
                                    <FormGroup style={{ flex: '1 1 200px' }}>
                                        <Label>Internal Name</Label>
                                        <Input
                                            maxLength={100}
                                            placeholder="widgets"
                                            value={form.internalName}
                                            onChange={(e) => handleFieldChange('internalName', e.target.value)}
                                        />
                                    </FormGroup>
                                    <FormGroup style={{ flex: '1 1 150px' }}>
                                        <Label>Data Type</Label>
                                        <Select
                                            value={dataTypeOptions.find((option) => option.value === form.dataType) || null}
                                            onChange={(option) => handleFieldChange('dataType', option.value)}
                                            options={dataTypeOptions}
                                        />
                                    </FormGroup>
                                </div>

                                {form.dataType === 'List' && (
                                    <>
                                        <Label>Options</Label>
                                        <Table responsive size="sm" className="mb-2">
                                            <thead>
                                                <tr>
                                                    <th>Value (sent to the SQL)</th>
                                                    <th>Label (shown to the user)</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {form.options.map((option, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <Input
                                                                bsSize="sm"
                                                                placeholder="high_cost"
                                                                value={option.value}
                                                                onChange={(e) =>
                                                                    handleOptionChange(index, 'value', e.target.value)
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <Input
                                                                bsSize="sm"
                                                                placeholder="Authorization"
                                                                value={option.label}
                                                                onChange={(e) =>
                                                                    handleOptionChange(index, 'label', e.target.value)
                                                                }
                                                            />
                                                        </td>
                                                        <td className="text-right">
                                                            <Button
                                                                color="link"
                                                                size="sm"
                                                                className="text-danger p-0"
                                                                title="Remove option"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => removeOption(index)}
                                                            >
                                                                <MdDeleteOutline style={{ fontSize: '18px' }} />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        <Button
                                            color="secondary"
                                            outline
                                            size="sm"
                                            className="mb-3"
                                            onClick={addOption}
                                        >
                                            Add Option
                                        </Button>
                                    </>
                                )}

                                <div className="d-flex justify-content-end align-items-center" style={{ gap: '12px' }}>
                                    {formError && <span className="text-danger small mr-auto">{formError}</span>}
                                    <Button
                                        color="primary"
                                        size="sm"
                                        disabled={isSaving || isUnchanged || Boolean(formError)}
                                        onClick={handleSave}
                                        className="d-flex align-items-center"
                                        style={{ gap: '6px', cursor: 'pointer' }}
                                    >
                                        {isSaving && <ButtonLoader />}
                                        {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Save'}
                                    </Button>
                                    <Button
                                        color="secondary"
                                        outline
                                        size="sm"
                                        disabled={isSaving}
                                        onClick={closeForm}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}

                        {sharedParameters.length > 0 ? (
                            <div className="px-3">
                            <Table responsive size="sm" className="pharmacy-reports-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Internal Name</th>
                                        <th>Data Type</th>
                                        <th>Options</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sharedParameters.map((sharedParameter) => (
                                        <tr key={sharedParameter.internalReportSharedParameterId}>
                                            <td>{sharedParameter.name}</td>
                                            <td>{sharedParameter.internalName}</td>
                                            <td>{sharedParameter.dataType}</td>
                                            <td>
                                                {(sharedParameter.options || [])
                                                    .map((option) => option.label)
                                                    .join(', ') || '-'}
                                            </td>
                                            <td className="text-right text-nowrap">
                                                {canEdit && (
                                                    <Button
                                                        color="link"
                                                        size="sm"
                                                        className="p-0 mr-3"
                                                        title="Edit shared parameter"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => openEditForm(sharedParameter)}
                                                    >
                                                        <MdEdit style={{ fontSize: '18px' }} />
                                                    </Button>
                                                )}
                                                {canDelete && (
                                                    <Button
                                                        color="link"
                                                        size="sm"
                                                        className="text-danger p-0"
                                                        title="Delete shared parameter"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleDelete(sharedParameter)}
                                                    >
                                                        <MdDeleteOutline style={{ fontSize: '18px' }} />
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            </div>
                        ) : (
                            !isFormOpen && <div className="text-muted text-center py-4">No shared parameters yet.</div>
                        )}
                    </>
                )}
            </ModalBody>
        </Modal>
    );
}

export default SharedParametersModal;
